import { Flex } from '@sendoutcards/quantum-design-ui'
import React from 'react'
import { Widget } from 'src/dashboard/components/Widgets/Widget'
import { SentCardSendType } from 'src/graphql/generated/graphql'
import { useActions } from 'src/hooks'
import { usePaginatedSentCards } from 'src/react_query'
import { Button } from 'src/design_system/components/Button/Button'
import { CardLineItem } from './CardLineItem'

export const CardHistoryWidget = ({ fillHeight }: { fillHeight?: boolean }) => {
  const actions = useActions()
  const cardHistoryQuery = usePaginatedSentCards({
    page: 1,
    search: '',
    contacts: [],
    sendType: SentCardSendType.Heartfelt,
  })

  const parseDateToMs = (value: string) => {
    const parsed = Date.parse(value)
    if (!Number.isNaN(parsed)) return parsed
    const asDateOnly = Date.parse(`${value}T00:00:00Z`)
    return Number.isNaN(asDateOnly) ? undefined : asDateOnly
  }

  const now = new Date()
  const sixMonthsAgo = new Date(now)
  sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6)

  const sentCards = cardHistoryQuery.data?.sentCards ?? []
  let mostRecentSentAtMs: number | undefined
  for (const sentCard of sentCards) {
    const dateToSend = sentCard?.dateToSend
    if (!dateToSend) continue
    const sentAtMs = parseDateToMs(dateToSend)
    if (
      sentAtMs !== undefined &&
      (mostRecentSentAtMs === undefined || sentAtMs > mostRecentSentAtMs)
    ) {
      mostRecentSentAtMs = sentAtMs
    }
  }
  const isStaleOverSixMonths =
    mostRecentSentAtMs !== undefined &&
    mostRecentSentAtMs < sixMonthsAgo.getTime()

  const sentCardsToRender = isStaleOverSixMonths ? [] : sentCards

  const cards = sentCardsToRender.slice(0, 3).map(res => {
    const card = res?.recipient?.card
    return {
      preview: card?.frontPreviewUrl ?? '',
      isHorizontal: card?.isHorizontal ?? false,
      sendableCard: { title: card?.sendableCard?.title ?? 'My Card' },
      date: res?.dateToSend ?? '',
      status: res?.status ?? undefined,
    }
  })

  const isEmpty = isStaleOverSixMonths || sentCards.length === 0

  return (
    <Widget
      minWidth="340px"
      isLoading={cardHistoryQuery.isLoading}
      borderRadiusOverride="32px"
      isEmpty={isEmpty}
      height={fillHeight ? '100%' : isEmpty ? '380px' : undefined}
      header={{
        title: 'Recent Cards Sent',
        action:
          isEmpty || cardHistoryQuery.isLoading
            ? undefined
            : {
                title: 'View More',
                onClick: () => actions.openCardHistory(),
              },
      }}
      renderEmpty={() => (
        <Flex
          width="100%"
          flexDirection="column"
          style={{ flex: 1, minHeight: 0 }}
        >
          <Flex
            width="100%"
            justifyContent="center"
            alignItems="center"
            style={{ flex: 1, minHeight: 0 }}
          >
            <Flex width="100%" css={{ display: 'grid' }}>
              <Widget
                shouldHideShadow={true}
                backgroundColor="#F8F9FA"
                borderRadiusOverride="24px"
                paddingOverride="24px 16px"
                isEmpty={true}
                emptyState={{
                  title: isStaleOverSixMonths
                    ? "It's been a while!"
                    : 'Ready to make someone’s day?',
                  description:
                    'Browse the catalog, pick a design, and share a little gratitude with someone you care about.',
                  icon: 'nav_catalog',
                }}
              />
            </Flex>
          </Flex>
          <Flex
            width="100%"
            style={{ paddingTop: 16, marginTop: 'auto' }}
            justifyContent="center"
          >
            <Button
              title="Go to Catalog"
              textSize="12px"
              padding="8px 28px"
              height="38px"
              background="#BE9CDD"
              textColor="#fff"
              onClick={() => actions.openCatalog()}
              icon={{ name: 'nav_catalog', color: '#fff', size: 21 }}
            />
          </Flex>
        </Flex>
      )}
      emptyState={{
        title: '',
        description: '',
        icon: 'exp_calendar',
      }}
    >
      <Flex flexDirection="column" width="100%" alignItems="flex-start">
        {cards &&
          cards.map((card, index) => {
            return (
              <Flex
                key={index}
                width="inherit"
                justifyContent="flex-start"
                alignItems="flex-start"
              >
                <CardLineItem
                  key={index}
                  color="#378487"
                  card={{
                    name: card.sendableCard.title,
                    isHorizontal: card.isHorizontal,
                    thumbnail: card?.preview,
                  }}
                  status={card?.status}
                  date={card.date}
                />
              </Flex>
            )
          })}
      </Flex>
    </Widget>
  )
}
